import DESIGN_EN from './locales/en.json'
import type { Catalogue, Locale } from './types'

/**
 * The design system's own strings.
 *
 * ## Why a component library has a catalogue at all
 *
 * The rule this follows is that **the design system ships label keys and never copy**. A
 * component with `emptyLabel = 'Any'` baked in as a default is a component that renders
 * English in a Mongolian page, and it does it invisibly: the prop is optional, so no call
 * site is obviously wrong and nothing fails.
 *
 * So the components take their strings as required props, and the words live here — in one
 * namespace, `ul.*`, that every app merges into its own catalogue. A translator opens one
 * file per language per app and finds the whole app in it, chrome included.
 *
 * ## Merging
 *
 * `mergeCatalogues` puts the app's entries **after** these, so an app may override any
 * `ul.*` string without editing the design system. Nothing does today; the resort will, the
 * first time "Any" is the wrong word for a room.
 */
export const DESIGN_CATALOGUE_EN: Catalogue = DESIGN_EN

/** Every key the design system's own components need. */
export type DesignMessageKey = keyof typeof DESIGN_EN

/**
 * The design system's OTHER languages, lazily — one chunk each, exactly as an app's are.
 *
 * ## This was missing, and its absence was invisible
 *
 * `DESIGN_CATALOGUE_EN` went into `base` from the first day, so the `ul.*` keys had an
 * English fallback and every component rendered. What no code path did was *load* the
 * Mongolian beside it: `LocaleProvider` asked the **app** for a chunk and merged nothing
 * from here, so `mn.json` in this folder was a file nothing imported.
 *
 * Two consequences, both silent. Every `ul.*` string — the combobox's "Any", the language
 * selector's own name, the locale notice itself — stayed English inside a fully Mongolian
 * page. And `coverage` counts the covered keys of `base`, which *includes* these seven, so
 * an app whose own catalogue was translated to the last key still scored below 1 and
 * `LocaleNotice` told the reader the translation was unfinished forever.
 *
 * The English file is imported statically above and excluded here for the reason an app's
 * is: it is the fallback for every key in every language, so it belongs in the initial
 * bundle, and importing it both ways makes Rollup keep it there twice.
 *
 * ## Written out, rather than `import.meta.glob`
 *
 * The five apps use the glob, and should: it is Vite's, it is one line, and a new locale
 * file is picked up by dropping it in the folder. **This package is not a Vite app.** It
 * is consumed as source, it carries no `vite/client` types, and `import.meta.glob` does
 * not typecheck here — adding the bundler's types to the design system to save two lines
 * would make every future consumer need them too, including React Native.
 *
 * A plain dynamic `import()` is ordinary ESM, so it needs nothing, and Vite still gives it
 * a chunk of its own. The cost is that a third language is a line here as well as a file,
 * which `LOCALES` already makes true in `types.ts` and `Flag.tsx`.
 */
export const DESIGN_LOADERS: Partial<
  Record<Locale, () => Promise<{ default: Catalogue }>>
> = {
  mn: () => import('./locales/mn.json').then((mod) => ({ default: mod.default as Catalogue })),
}

/**
 * The design system's strings, then the app's. Later wins, so an app can override.
 *
 * Both are partial by design: a locale file that is half translated is legitimate and
 * ships, because `t` falls back to English per key rather than per file.
 */
export const mergeCatalogues = (
  design: Catalogue,
  app: Catalogue,
): Catalogue => ({ ...design, ...app })

/** The design system's catalogue for one locale, given its own locale files. */
export type DesignCatalogues = Readonly<Record<Locale, Catalogue>>
